import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Youtube, Mail, Heart, Disc3 } from 'lucide-react';
import { FOOTER, HOME } from '@/constants/testIds';
import { PLAYER_LINKS, PAYPAL_URL, SOCIAL, CONTACT_EMAIL } from '@/constants/stations';

const StreamLink = ({ href, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="px-3 py-1.5 rounded-full border border-amber/15 text-xs text-cream/70 hover:text-amber hover:border-amber/50 transition-colors font-body uppercase tracking-wider"
  >
    {label}
  </a>
);

const Footer = () => {
  return (
    <footer data-testid={FOOTER.root} className="relative mt-20 border-t border-amber/10 bg-vinyl overflow-hidden">
      {/* Giant background text */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <div className="font-display text-[18vw] leading-none text-amber/[0.035] whitespace-nowrap">
          MAJESTIC
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <Disc3 className="w-6 h-6 text-amber" />
            <span className="font-display text-2xl text-cream">Majestic Jukebox Radio</span>
          </div>
          <p className="mt-4 text-cream/60 font-body max-w-md leading-relaxed">
            An independent radio station spinning unforgettable songs from the 40s to the 80s — oldies,
            jazz, swing, rock and roll, blues, country, doo wop and big band, only in the
            best possible sound.
          </p>
          <div className="flex items-center gap-3 mt-6">
            <a
              data-testid={HOME.facebookLink}
              href={SOCIAL.facebook}
              target="_blank" rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border border-amber/20 flex items-center justify-center text-cream/70 hover:text-amber hover:border-amber hover:-translate-y-0.5 transition-all"
              aria-label="Facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              data-testid={HOME.youtubeLink}
              href={SOCIAL.youtube}
              target="_blank" rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border border-amber/20 flex items-center justify-center text-cream/70 hover:text-amber hover:border-amber hover:-translate-y-0.5 transition-all"
              aria-label="YouTube"
            >
              <Youtube className="w-4 h-4" />
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="w-10 h-10 rounded-full border border-amber/20 flex items-center justify-center text-cream/70 hover:text-amber hover:border-amber hover:-translate-y-0.5 transition-all"
              aria-label="Email"
            >
              <Mail className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-4">Navigation</div>
          <ul className="space-y-3 font-body text-cream/70">
            <li><Link to="/" className="hover:text-amber transition">Home</Link></li>
            <li><Link to="/about" className="hover:text-amber transition">About</Link></li>
            <li><Link to="/contact" className="hover:text-amber transition">Contact</Link></li>
            <li>
              <a
                data-testid={HOME.oldiesRepertoryLink}
                href="https://www.oldiesrepertory.com/"
                target="_blank" rel="noopener noreferrer"
                className="hover:text-amber transition"
              >
                Oldies Repertory ↗
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-4">Listen Anywhere</div>
          <div className="flex flex-wrap gap-2">
            {PLAYER_LINKS.map((l) => (
              <StreamLink key={l.id} href={l.href} label={l.label.replace(' / iTunes', '')} />
            ))}
          </div>

          <a
            data-testid={FOOTER.paypalLink}
            href={PAYPAL_URL}
            target="_blank" rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-amber-light hover:text-amber transition font-body text-sm"
          >
            <Heart className="w-4 h-4" fill="currentColor" /> Support our station via PayPal
          </a>
        </div>
      </div>

      <div className="relative border-t border-amber/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="text-xs text-cream/40 font-body">
            © Majestic Jukebox Radio. All rights reserved.
          </div>
          <div className="text-xs text-cream/40 font-body italic">
            On air since December 2017.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
